import { createRouter, createWebHistory } from 'vue-router';
import homeRoutes from "./homeRoutes";
import authRoutes from "./authRoutes";
import dashboardRoutes from "./dashboardRoutes";
import integrationRoutes from "./integrationRoutes";
import crmRoutes from './crmRoutes';
import notFoundRoutes from "./notFoundRoutes";
import catalogRoutes from "./catalogRoutes";
import settingsRoutes from "./settingsRoutes";
import managementRoutes from "./managementRoutes";
import analyticsRoutes from "./analyticsRoutes";
import store from '../store/index';
import i18n from '../i18n';
import {httpClient} from '../../services/HttpClient';
import {generateUrl} from '../../utils';
import ProjectLayout from '../layouts/ProjectLayout.vue';
import withLayout from './withLayout';

// Pages that no longer need the browser to sit on the project subdomain: their own endpoints
// take the project from the path, and whatever legacy endpoints they still call are rewritten
// to the subdomain by the HttpClient interceptor. Add a route name here once its backend
// routes have been migrated to /projects/{project}/...
const HOST_AGNOSTIC_ROUTES = [
    'CategoriesCard',
    'CreateCategoryPage',
    'CreateIntegrationFilterCard',
    'EditIntegrationFilterCard',
    'CreateIntegrationFilterItemCard',
    'EditIntegrationFilterItemCard',
    'CustomFieldsIndexPage',
    'CustomFieldCreatePage',
    'CustomFieldEditPage',
    'PriceTemplatesIndexPage',
    'PriceTemplateCreatePage',
    'PriceTemplateEditPage',
];

const routes = [
    ...authRoutes,
    ...homeRoutes,
    withLayout(ProjectLayout, [
        integrationRoutes,
        crmRoutes,
        catalogRoutes,
        settingsRoutes,
        managementRoutes,
        analyticsRoutes,
        dashboardRoutes,
    ]),
    ...notFoundRoutes,
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

httpClient.setNotFoundHandler(() => {
    router.push({ name: 'NotFoundPage' });
});

router.beforeEach(async (to, from, next) => {
    let defaultTitle = 'Fasunok';


    if (to.params.projectUuid) {
        if (store.state.route.project?.uuid !== to.params.projectUuid) {
            // Project not loaded yet — fetch it via the host-agnostic route on the current host.
            httpClient.clearProjectSubdomain();
            await store.dispatch('route/fetchProject', to.params.projectUuid);
        }

        const alias = store.state.route.project?.alias;

        // Project pages live on the project subdomain (the tenant is resolved from the host).
        // If we are not on it — put the subdomain into the route via a full page load, unless
        // the page is already host-agnostic.
        if (alias && !HOST_AGNOSTIC_ROUTES.includes(to.name) && window.location.hostname.split('.')[0] !== alias) {
            window.location.href = generateUrl(to.fullPath.replace(/^\/+/, ''), alias);
            return;
        }

        // From here on all alias-dependent requests of this project go to its subdomain.
        httpClient.setProjectSubdomain(alias);
    } else {
        httpClient.clearProjectSubdomain();
    }

    // The layout route (see withLayout.js) is an implementation detail, not a real
    // page — take the first matched route below it, matching pre-nesting behavior
    // where `matched[0]` was always the outermost real page/hub route.
    const pageMatch = to.matched.find((route) => !route.meta?.isLayout) ?? to.matched[to.matched.length - 1];

    const pageTitle = pageMatch.meta.breadcrumb()[0]?.name;

    if (store.state.route?.project?.name) {
        defaultTitle = pageTitle
            ? store.state.route?.project?.name + " - " + pageTitle + " - " + defaultTitle
            : store.state.route?.project?.name + " - " + defaultTitle;
    } else {
        defaultTitle = pageTitle ? pageTitle + " - " + defaultTitle : defaultTitle;
    }

    document.title = defaultTitle;

    next();
});

export default router;
